// =============================================================================
// FILE: packages/renderer/src/lib/calibration/CalibrationEngine.ts
// PURPOSE: Cheap, fire-and-forget startup self-test.
//
//   Runs once after the renderer mounts. Each check is timeboxed; the
//   whole sweep MUST NOT block the UI. Result severity drives the
//   indicator in the top bar (🟢 / 🟡 / 🔴).
//
//   Adding a new check is a single push to BUILT_IN_CHECKS — keep them
//   under 100 ms each.
// =============================================================================

import { call, IpcCallError } from '../api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';

export type CalibrationStatus = 'pass' | 'fail' | 'warn';
export type CalibrationSeverity = 'critical' | 'warning' | 'info';

export interface CalibrationItem {
  name:        string;
  category:    'ipc' | 'module' | 'dialog' | 'ui';
  severity:    CalibrationSeverity;
  status:      CalibrationStatus;
  error?:      string;
  durationMs:  number;
}

export interface CalibrationReport {
  passed:      boolean;
  ranAt:       string;
  results:     CalibrationItem[];
  counts: { pass: number; warn: number; fail: number; critical: number };
}

type CheckFn = () => Promise<void>;
interface CheckDef {
  name:     string;
  category: CalibrationItem['category'];
  severity: CalibrationSeverity;
  timeoutMs: number;
  run:      CheckFn;
}

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`timed out after ${ms}ms (${label})`)), ms);
    p.then((v) => { clearTimeout(t); resolve(v); },
           (e) => { clearTimeout(t); reject(e as Error); });
  });
}

// ---------------------------------------------------------------------------
// Built-in checklist. Each check should resolve fast (<100 ms).
// ---------------------------------------------------------------------------
const BUILT_IN_CHECKS: CheckDef[] = [
  // IPC
  {
    name: 'IPC ping', category: 'ipc', severity: 'critical', timeoutMs: 1_500,
    run: async () => {
      const r = await call<undefined, { ok: boolean }>(IPC.PING_MAIN_PROCESS, undefined);
      if (!r?.ok) throw new Error('ping returned non-ok');
    },
  },
  {
    name: 'Folder Monitor status', category: 'ipc', severity: 'warning', timeoutMs: 1_500,
    run: async () => {
      await call(IPC.FOLDER_MONITOR_STATUS, undefined);
    },
  },
  {
    name: 'Boot probe (project / system channels)', category: 'ipc', severity: 'critical', timeoutMs: 2_000,
    run: async () => {
      const required: string[] = [
        IPC.PROJECT_CREATE, IPC.PROJECT_GET, IPC.PROJECT_LIST,
        IPC.SYSTEM_RESET_WORKSPACE, IPC.SYSTEM_PORTABLE_INFO,
      ];
      const r = await call<{ channels: string[] }, { missing: string[] } | null>(
        IPC.SYSTEM_BOOT_PROBE, { channels: required });
      if (r?.missing && r.missing.length > 0) {
        throw new Error(`missing handlers: ${r.missing.join(', ')}`);
      }
    },
  },
  // Lazy modules — dynamic imports of the actual page chunks. Throws if a
  // chunk hash referenced by the entry bundle is missing on disk.
  {
    name: 'BuildingsPage chunk loads', category: 'module', severity: 'critical', timeoutMs: 3_000,
    run: async () => { await import('../../pages/BuildingsPage.js'); },
  },
  {
    name: 'AssetsPage chunk loads', category: 'module', severity: 'critical', timeoutMs: 3_000,
    run: async () => { await import('../../pages/AssetsPage.js'); },
  },
  {
    name: 'SyncPage chunk loads', category: 'module', severity: 'warning', timeoutMs: 3_000,
    run: async () => { await import('../../pages/SyncPage.js'); },
  },
  // Dialogs — confirm the modules parse + export the component symbol.
  {
    name: 'NewBuildingDialog module loads', category: 'dialog', severity: 'critical', timeoutMs: 3_000,
    run: async () => {
      const m = await import('../../components/NewBuildingDialog.js');
      if (typeof (m as { NewBuildingDialog?: unknown }).NewBuildingDialog !== 'function') {
        throw new Error('NewBuildingDialog export missing');
      }
    },
  },
  {
    name: 'NewAssetDialog module loads', category: 'dialog', severity: 'critical', timeoutMs: 3_000,
    run: async () => {
      const m = await import('../../components/NewAssetDialog.js');
      if (typeof (m as { NewAssetDialog?: unknown }).NewAssetDialog !== 'function') {
        throw new Error('NewAssetDialog export missing');
      }
    },
  },
  // ---------------------------------------------------------------------------
  // Meeting Capture Engine — non-blocking warnings. A missing microphone or
  // unanswered IPC must NOT prevent BIM OPS from launching; the renderer
  // simply shows a yellow status pill so QA can spot the issue early.
  // ---------------------------------------------------------------------------
  {
    name: 'MeetingCaptureEngine IPC reachable',
    category: 'ipc', severity: 'warning', timeoutMs: 2_000,
    run: async () => {
      const required = [
        IPC.MEETING_START, IPC.MEETING_STOP, IPC.MEETING_SAVE_AUDIO,
        IPC.MEETING_LIST,  IPC.MEETING_READ_AUDIO, IPC.MEETING_ADD_NOTE,
      ];
      const r = await call<{ channels: string[] }, { missing: string[] } | null>(
        IPC.SYSTEM_BOOT_PROBE, { channels: required });
      if (r?.missing && r.missing.length > 0) {
        throw new Error(`missing meeting handlers: ${r.missing.join(', ')}`);
      }
    },
  },
  {
    name: 'Microphone device available (probe-only, non-blocking)',
    category: 'ui', severity: 'warning', timeoutMs: 2_000,
    run: async () => {
      // Permissions API check — never calls getUserMedia, so the OS will not
      // pop a permission prompt during boot. State 'denied' surfaces a yellow
      // warning so QA notices before they try to record.
      const perms = (navigator as unknown as { permissions?: { query: (q: { name: string }) =>
        Promise<{ state: 'granted'|'denied'|'prompt' }> } }).permissions;
      if (!perms) return;  // older browsers — treat as pass
      const r = await perms.query({ name: 'microphone' });
      if (r.state === 'denied') {
        throw new Error('microphone permission denied at the OS level');
      }
    },
  },
  {
    name: 'CalendarEngine IPC reachable',
    category: 'ipc', severity: 'warning', timeoutMs: 2_000,
    run: async () => {
      const required = [IPC.CAL_LIST, IPC.CAL_UPCOMING, IPC.CAL_TODAY, IPC.CAL_PROVIDER_STATE];
      const r = await call<{ channels: string[] }, { missing: string[] } | null>(
        IPC.SYSTEM_BOOT_PROBE, { channels: required });
      if (r?.missing && r.missing.length > 0) {
        throw new Error(`missing calendar handlers: ${r.missing.join(', ')}`);
      }
    },
  },
];

export class CalibrationEngine {
  private lastReport: CalibrationReport | null = null;

  async run(extra: CheckDef[] = []): Promise<CalibrationReport> {
    const checks = [...BUILT_IN_CHECKS, ...extra];
    const results: CalibrationItem[] = [];
    for (const check of checks) {
      const startedAt = performance.now();
      try {
        await withTimeout(check.run(), check.timeoutMs, check.name);
        results.push({
          name: check.name, category: check.category, severity: check.severity,
          status: 'pass', durationMs: Math.round(performance.now() - startedAt),
        });
      } catch (err) {
        const msg = err instanceof IpcCallError ? `${err.code}: ${err.message}`
                  : err instanceof Error        ? err.message
                  :                                String(err);
        results.push({
          name: check.name, category: check.category, severity: check.severity,
          status: check.severity === 'warning' ? 'warn' : 'fail',
          error: msg, durationMs: Math.round(performance.now() - startedAt),
        });
      }
    }
    const counts = {
      pass: results.filter((r) => r.status === 'pass').length,
      warn: results.filter((r) => r.status === 'warn').length,
      fail: results.filter((r) => r.status === 'fail').length,
      critical: results.filter((r) => r.status === 'fail' && r.severity === 'critical').length,
    };
    const report: CalibrationReport = {
      passed: counts.fail === 0,
      ranAt:  new Date().toISOString(),
      results, counts,
    };
    this.lastReport = report;
    return report;
  }

  getLastReport(): CalibrationReport | null { return this.lastReport; }
}

export const calibrationEngine = new CalibrationEngine();
